import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../dashboard/DashboardLayout';
import { 
  User, Mail, MapPin, Globe, Linkedin, 
  Edit2, Save, Upload, Briefcase, Award, 
  ChevronRight, ExternalLink, Github as GithubIcon,
  Zap,
  ShieldCheck,
  Target,
  BadgeCheck
} from 'lucide-react';
import { trackPageView, trackButtonClick } from '../../utils/analytics';
import axiosInstance from '../../utils/axiosInstance';
import { RefreshCw as LoaderIcon } from 'lucide-react';

const ProfilePage = () => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    location: '',
    bio: '',
    skills: [],
    linkedin: '',
    github: '',
    portfolio: '',
    avatar: null,
    tier: 'starter'
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  useEffect(() => {
    trackPageView("/profile", "User Profile");
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const resp = await axiosInstance.get('/user');
      const d = resp.data;
      
      // Parse URLs if it was stored as JSON in urls field or just use separate fields
      let linkedin = '';
      let github = '';
      let portfolio = '';
      
      if (d.urls && Array.isArray(d.urls)) {
        linkedin = d.urls.find(u => u.includes('linkedin')) || '';
        github = d.urls.find(u => u.includes('github')) || '';
        portfolio = d.urls.find(u => !u.includes('linkedin') && !u.includes('github')) || '';
      }

      setProfile({
        firstName: d.first_name || '',
        lastName: d.last_name || '',
        email: d.email || '',
        role: d.role || '',
        location: d.location || '',
        bio: d.bio || '',
        skills: d.skills || [],
        linkedin: linkedin,
        github: github,
        portfolio: portfolio,
        avatar: d.profile_picture_url || null,
        tier: d.tier || 'starter'
      });
      setAvatarPreview(d.profile_picture_url || null);
    } catch (e) {
      setError("Failed to load profile intelligence.");
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTabChange = (tabId) => {
    if (tabId === 'profile') return;
    navigate('/dashboard', { state: { tab: tabId } });
  };

  const handleSave = async () => {
    trackButtonClick("Save Profile", "Profile");
    setSaving(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append('first_name', profile.firstName);
      formData.append('last_name', profile.lastName);
      formData.append('email', profile.email);
      formData.append('role', profile.role);
      formData.append('location', profile.location);
      formData.append('bio', profile.bio);
      
      if (profile.skills && profile.skills.length > 0) {
        formData.append('skills', JSON.stringify(profile.skills));
      }
      
      const urls = [profile.linkedin, profile.github, profile.portfolio].filter(u => u);
      formData.append('urls', JSON.stringify(urls));

      if (avatarFile) {
        formData.append('profile_picture', avatarFile);
      }

      await axiosInstance.patch('/user', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setIsEditing(false);
      fetchProfile(); // Refresh to get the actual S3 URL
    } catch (e) {
      setError("Failed to synchronize profile data.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout activeTab="profile">
        <div className="h-[60vh] flex flex-col items-center justify-center gap-4">
          <LoaderIcon size={40} className="text-blue-600 animate-spin" />
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest italic animate-pulse">Syncing Profile Intelligence...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout activeTab="profile" setTab={handleTabChange}>
      <div className="max-w-7xl mx-auto space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20 font-sans">
        
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
           <div className="flex flex-col text-center md:text-left">
              <h2 className="text-3xl font-black text-slate-900 tracking-tight italic flex items-center justify-center md:justify-start gap-3">
                 <Target size={28} className="text-blue-600" /> User Profile
              </h2>
              <div className="flex items-center justify-center md:justify-start gap-3 mt-1">
                 <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Active Status</span>
                 </div>
              </div>
           </div>

           <button
             onClick={() => isEditing ? handleSave() : setIsEditing(true)}
             disabled={saving}
             className={`px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all italic active:scale-95 flex items-center gap-2 shadow-xl ${
               isEditing 
                ? 'bg-blue-600 text-white hover:bg-black shadow-blue-100' 
                : 'bg-slate-900 text-white hover:bg-black shadow-slate-200'
             }`}
           >
             {saving ? <><LoaderIcon size={16} className="animate-spin" /> Syncing...</> : 
              isEditing ? <><Save size={16} /> Save Profile</> : <><Edit2 size={16} /> Edit Profile</>}
           </button>
        </div>

        {error && (
          <div className="p-4 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3 text-red-700 font-bold animate-in slide-in-from-top-2">
             <Target size={18} /> <span className="text-sm">{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          
          {/* Identity Core Card */}
          <div className="lg:col-span-4 space-y-10">
            <div className="bg-white border border-slate-200 rounded-[3rem] p-10 shadow-sm relative overflow-hidden group">
              <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-br from-slate-50 to-blue-50/50 -z-0" />
              
              <div className="relative z-10 flex flex-col items-center">
                 <div className="relative mb-8 group/avatar">
                    <div className="w-40 h-40 rounded-[2.5rem] bg-white p-1.5 shadow-2xl overflow-hidden ring-1 ring-slate-100 group-hover/avatar:rotate-3 transition-transform duration-500">
                       <div className="w-full h-full rounded-[2rem] bg-slate-50 flex items-center justify-center overflow-hidden">
                          {avatarPreview ? (
                             <img src={avatarPreview} alt="Profile" className="w-full h-full object-cover" />
                          ) : (
                             <User size={80} className="text-slate-200" />
                          )}
                       </div>
                    </div>
                    {isEditing && (
                       <>
                         <input 
                           type="file" 
                           id="avatar-upload" 
                           className="hidden" 
                           accept="image/*"
                           onChange={handleAvatarChange}
                         />
                         <label 
                           htmlFor="avatar-upload"
                           className="absolute -bottom-2 -right-2 w-12 h-12 bg-blue-600 text-white rounded-2xl shadow-xl flex items-center justify-center hover:bg-black transition-all ring-8 ring-white cursor-pointer"
                         >
                            <Upload size={20} />
                         </label>
                       </>
                    )}
                 </div>
                 
                 <h3 className="text-2xl font-black text-slate-900 italic tracking-tight">{profile.firstName} {profile.lastName}</h3>
                 <div className="flex flex-col items-center gap-3 mt-4">
                    <div className="flex items-center gap-2">
                       <BadgeCheck size={14} className="text-blue-500 fill-blue-50" />
                       <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest leading-none">{profile.role}</span>
                    </div>
                    <div className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-[0.2em] shadow-lg italic ${
                       profile.tier === 'elite' ? 'bg-slate-900 text-white' : 
                       profile.tier === 'pro' ? 'bg-blue-600 text-white shadow-blue-500/10' : 
                       'bg-slate-50 text-slate-400'
                    }`}>
                       {profile.tier === 'elite' ? 'Enterprise' : profile.tier} Plan
                    </div>
                 </div>

                 <div className="w-full mt-10 space-y-6 pt-10 border-t border-slate-50">
                    <div className="group flex items-center gap-4 cursor-pointer">
                       <div className="w-10 h-10 bg-slate-50 text-slate-400 rounded-xl flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-all shadow-inner">
                          <Mail size={18} />
                       </div>
                       <div className="min-w-0">
                          <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Email</div>
                          <div className="text-xs font-black text-slate-900 italic truncate">{profile.email}</div>
                       </div>
                    </div>
                    <div className="group flex items-center gap-4 cursor-pointer">
                       <div className="w-10 h-10 bg-slate-50 text-slate-400 rounded-xl flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-all shadow-inner">
                          <MapPin size={18} />
                       </div>
                       <div className="min-w-0">
                          <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Location</div>
                          <div className="text-xs font-black text-slate-900 italic truncate">{profile.location}</div>
                       </div>
                    </div>
                    <div className="group flex items-center gap-4 cursor-pointer">
                       <div className="w-10 h-10 bg-slate-50 text-slate-400 rounded-xl flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-all shadow-inner">
                          <Globe size={18} />
                       </div>
                       <div className="min-w-0">
                          <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Website</div>
                          <a href={profile.portfolio} target="_blank" rel="noreferrer" className="text-xs font-black text-blue-600 italic hover:underline flex items-center gap-1.5">
                             Public Profile <ExternalLink size={12} />
                          </a>
                       </div>
                    </div>
                 </div>
              </div>
            </div>

            <div className="bg-slate-900 rounded-[3rem] p-10 text-white shadow-2xl relative overflow-hidden border border-slate-800">
               <div className="absolute top-0 right-0 p-10 opacity-10 pointer-events-none -rotate-12"><Zap size={140} className="text-blue-500" /></div>
               <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-8 flex items-center gap-2 italic relative z-10">
                  <Award size={16} /> Skills
               </h4>
               <div className="flex flex-wrap gap-2 relative z-10">
                 {profile.skills.map(skill => (
                   <span key={skill} className="px-4 py-2 bg-white/5 border border-white/10 text-slate-400 rounded-xl text-[10px] font-black uppercase tracking-tight italic hover:bg-white/10 transition-all cursor-default">
                     {skill}
                   </span>
                 ))}
                 {isEditing && (
                    <button 
                      onClick={() => {
                        const s = prompt("Enter new skill:");
                        if (s) setProfile({...profile, skills: [...profile.skills, s]});
                      }}
                      className="px-4 py-2 bg-white/5 border-2 border-dashed border-white/10 text-slate-600 rounded-xl text-[10px] font-black uppercase italic hover:border-blue-500 hover:text-blue-500 transition-all"
                    >
                       + Add Skill
                    </button>
                 )}
               </div>
            </div>
          </div>

          {/* Details & Directives */}
          <div className="lg:col-span-8 space-y-10">
             <div className="bg-white border border-slate-200 rounded-[3rem] p-10 shadow-sm relative overflow-hidden group">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-10 flex items-center gap-3 italic">
                   <Briefcase size={18} className="text-blue-600" /> Professional Details
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {[
                    { label: 'First Name', key: 'firstName', type: 'text' },
                    { label: 'Last Name', key: 'lastName', type: 'text' },
                    { label: 'Primary Role', key: 'role', type: 'text' },
                    { label: 'Location City', key: 'location', type: 'text' }
                  ].map((field) => (
                    <div key={field.key} className="space-y-2">
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">{field.label}</label>
                      {isEditing ? (
                        <input 
                          type={field.type} 
                          value={profile[field.key]}
                          onChange={(e) => setProfile({...profile, [field.key]: e.target.value})}
                          className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-black outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-400 transition-all shadow-inner italic"
                        />
                      ) : (
                        <div className="text-sm text-slate-900 font-black italic p-4 bg-slate-50/50 rounded-2xl border border-slate-100">{profile[field.key]}</div>
                      )}
                    </div>
                  ))}

                  <div className="space-y-2 md:col-span-2">
                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">About Me (Bio)</label>
                    {isEditing ? (
                        <textarea 
                          rows={6}
                          value={profile.bio}
                          onChange={(e) => setProfile({...profile, bio: e.target.value})}
                          className="w-full px-6 py-5 bg-slate-50 border border-slate-200 rounded-[2rem] text-xs font-black outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-400 transition-all shadow-inner resize-none italic leading-relaxed"
                          placeholder="Tell us about your professional background..."
                        />
                    ) : (
                        <div className="text-sm text-slate-600 leading-relaxed p-8 bg-slate-50 rounded-[2.5rem] border border-slate-100 font-bold italic shadow-inner">
                           <div className="flex gap-2 mb-4"><Zap size={14} className="text-blue-500"/><Zap size={14} className="text-blue-500 opacity-50"/><Zap size={14} className="text-blue-500 opacity-20"/></div>
                           "{profile.bio}"
                        </div>
                    )}
                  </div>
                </div>
             </div>

             <div className="bg-white border border-slate-200 rounded-[3rem] p-10 shadow-sm">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-10 flex items-center gap-3 italic">
                   <ShieldCheck size={18} className="text-emerald-500" /> Linked Accounts
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-slate-50/50 border border-slate-100 p-6 rounded-[2rem] group hover:border-blue-400 transition-all flex items-center gap-6 shadow-sm">
                       <div className="w-14 h-14 bg-white shadow-xl shadow-blue-500/10 rounded-2xl flex items-center justify-center text-blue-600 group-hover:scale-110 transition-transform"><Linkedin size={28}/></div>
                       <div className="min-w-0 flex-1">
                          <div className="text-[10px] font-black text-slate-900 uppercase tracking-widest italic">LinkedIn</div>
                          {isEditing ? (
                            <input className="w-full mt-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-[10px] outline-none" value={profile.linkedin} onChange={e => setProfile({...profile, linkedin: e.target.value})} />
                          ) : (
                            <div className="text-[10px] font-black text-blue-500 truncate mt-1 italic">{profile.linkedin}</div>
                          )}
                       </div>
                       {!isEditing && <ChevronRight size={16} className="text-slate-300 group-hover:text-blue-600"/>}
                    </div>

                    <div className="bg-slate-50/50 border border-slate-100 p-6 rounded-[2rem] group hover:border-slate-800 transition-all flex items-center gap-6 shadow-sm">
                       <div className="w-14 h-14 bg-white shadow-xl shadow-slate-900/5 rounded-2xl flex items-center justify-center text-slate-900 group-hover:scale-110 transition-transform"><GithubIcon size={28}/></div>
                       <div className="min-w-0 flex-1">
                          <div className="text-[10px] font-black text-slate-900 uppercase tracking-widest italic">GitHub</div>
                          {isEditing ? (
                            <input className="w-full mt-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-[10px] outline-none" value={profile.github} onChange={e => setProfile({...profile, github: e.target.value})} />
                          ) : (
                            <div className="text-[10px] font-black text-slate-500 truncate mt-1 italic">{profile.github}</div>
                          )}
                       </div>
                       {!isEditing && <ChevronRight size={16} className="text-slate-300 group-hover:text-slate-900"/>}
                    </div>
                </div>
             </div>
          </div>

        </div>
      </div>
    </DashboardLayout>
  );
};

export default ProfilePage;